import React from "react";
import { Badge, Box, Button, SimpleGrid, Stack, Text, Textarea } from "@chakra-ui/react";
import { OktonAlert, OktonLoadingState, OktonPanel, useOktonAuth } from "@okton/platform-ui";
import { catalog, PlanError, request } from "./aiPlansApi";
import {
  AnalyticsReportStatus,
  confirmTextReport,
  executeText,
  getTextAvailability,
  getTextReportStatus,
  getTextUsage,
  loadTextRequest,
  newTextRequest,
  recoverText,
  saveTextRequest,
  textMoney,
  textStatuses,
  textStorageKey,
  TextExecution,
  TextIdentity,
  TextRequestIds,
  TextUsage,
} from "./aiPlanTextApi";
import { BranchContext, Catalog } from "./types";
import { analyticsReportUrl } from "./analyticsReportLink";

const confirmationLabels: Record<AnalyticsReportStatus["confirmation"]["state"], string> = {
  draft: "Aguardando confirmação",
  sending: "Salvando relatório",
  uncertain: "Em conferência",
  saved: "Relatório salvo",
  failed: "Criação rejeitada",
};
const confirmationColors: Record<AnalyticsReportStatus["confirmation"]["state"], string> = {
  draft: "yellow",
  sending: "blue",
  uncertain: "orange",
  saved: "green",
  failed: "red",
};
function reportFromExecution(execution: TextExecution | null): AnalyticsReportStatus | null {
  const value = execution?.result;
  if (!value?.plan || !value.preview || !value.confirmation) return null;
  return { plan: value.plan, preview: value.preview, confirmation: value.confirmation };
}

function formatFilterValue(value: unknown): string {
  const date = (item: unknown) => {
    if (typeof item !== "string") return item == null ? "" : String(item);
    const match = item.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return match ? `${match[3]}/${match[2]}/${match[1]}` : item;
  };
  if (Array.isArray(value)) return value.map(date).join(", ");
  if (value && typeof value === "object") {
    const range = value as { from?: unknown; to?: unknown };
    const from = date(range.from); const to = date(range.to);
    if (from && to) return `${from} até ${to}`;
    if (from) return `a partir de ${from}`;
    if (to) return `até ${to}`;
  }
  return date(value);
}

const formatTransientNumber = (value: number) =>
  new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 2 }).format(value);

export const AiPlanPilotPanel: React.FC = () => {
  const { accessToken, user } = useOktonAuth();
  const [loading,setLoading]=React.useState(true);
  const [error,setError]=React.useState("");
  const [context,setContext]=React.useState<BranchContext|null>(null);
  const [plan,setPlan]=React.useState<Catalog|null>(null);
  const [available,setAvailable]=React.useState(false);
  const [usage,setUsage]=React.useState<TextUsage|null>(null);
  const [input,setInput]=React.useState("Explique como funciona a emissão de NFS-e em lote.");
  const [sending,setSending]=React.useState(false);
  const [execution,setExecution]=React.useState<TextExecution|null>(null);
  const [reportStatus,setReportStatus]=React.useState<AnalyticsReportStatus|null>(null);
  const [ids,setIds]=React.useState<TextRequestIds|null>(null);
  const restoredRef=React.useRef("");

  const identity=React.useMemo<TextIdentity|null>(()=>{
    if(!user?.id||!context||!plan?.current?.id)return null;
    return {
      subject:user.id,
      system:"ai-plans",
      context,
      contractId:plan.current.id,
    };
  },[user,context,plan]);

  const refresh=React.useCallback(async(signal?:AbortSignal)=>{
    if(!accessToken)return;
    setLoading(true);setError("");
    try{
      const raw=await request("/v1/me/context/ai-plans",accessToken,"GET",undefined,signal);
      if(!raw||raw.companyId==null||raw.branchId==null)throw new Error("Selecione uma empresa e filial para o plano de IA.");
      const current:BranchContext={
        companyId:String(raw.companyId),
        branchId:String(raw.branchId),
        branchName:typeof raw.branchName==="string"?raw.branchName:undefined,
      };
      const cat=await catalog(accessToken,"ai-plans",current,signal);
      setContext(current);setPlan(cat);
      if(cat.current&&user?.id){
        const id:TextIdentity={subject:user.id,system:"ai-plans",context:current,contractId:cat.current.id};
        const [enabled,balance]=await Promise.all([
          getTextAvailability(accessToken,id,signal),
          getTextUsage(accessToken,id,signal),
        ]);
        setAvailable(enabled);setUsage(balance);
      }else{
        setAvailable(false);setUsage(null);
      }
    }catch(reason){
      if(!(reason instanceof DOMException&&reason.name==="AbortError")){
        setError(reason instanceof Error?reason.message:"Não foi possível carregar o piloto.");
      }
    }finally{setLoading(false);}
  },[accessToken,user]);

  React.useEffect(()=>{
    const controller=new AbortController();
    refresh(controller.signal);
    return()=>controller.abort();
  },[refresh]);

  const loadCurrent=React.useCallback(async(currentIdentity:TextIdentity,currentIds:TextRequestIds)=>{
    if(!accessToken)return;
    try{
      const status=await getTextReportStatus(accessToken,currentIdentity,currentIds);
      setReportStatus(status);
      setExecution(null);
    }catch(reason){
      if(!(reason instanceof PlanError)||reason.status!==404)throw reason;
      const result=await recoverText(accessToken,currentIdentity,currentIds);
      setExecution(result);
      setReportStatus(reportFromExecution(result));
    }
    setUsage(await getTextUsage(accessToken,currentIdentity));
  },[accessToken]);

  React.useEffect(()=>{
    if(!accessToken||!identity)return;
    const key=textStorageKey(identity);
    if(restoredRef.current===key)return;
    restoredRef.current=key;
    let active=true;
    try{
      const stored=loadTextRequest(key);
      if(!stored)return;
      setIds(stored);
      setSending(true);
      loadCurrent(identity,stored)
        .catch(reason=>{if(active)setError(reason instanceof Error?reason.message:"Falha ao recuperar a última solicitação.");})
        .finally(()=>{if(active)setSending(false);});
    }catch(reason){
      setError(reason instanceof Error?reason.message:"Não foi possível recuperar a solicitação salva.");
    }
    return()=>{active=false;};
  },[accessToken,identity,loadCurrent]);

  async function send(){
    if(!accessToken||!identity||sending||!input.trim())return;
    setSending(true);setError("");setExecution(null);setReportStatus(null);
    const requestIds=newTextRequest();
    try{
      const key=textStorageKey(identity);
      saveTextRequest(key,requestIds);
      setIds(requestIds);
      const result=await executeText(accessToken,identity,requestIds,input.trim());
      setExecution(result);
      setReportStatus(reportFromExecution(result));
      setUsage(await getTextUsage(accessToken,identity));
    }catch(reason){
      if(reason instanceof PlanError&&reason.status===409){
        try{await refresh();}catch{}
        setError(reason.message+" O contrato e o saldo foram atualizados; confirme os dados antes de enviar novamente.");
      }else{
        setError(reason instanceof Error?reason.message:"Falha ao executar o teste.");
      }
    }finally{setSending(false);}
  }

  async function recover(){
    if(!accessToken||!identity||!ids||sending)return;
    setSending(true);setError("");
    try{
      await loadCurrent(identity,ids);
    }catch(reason){
      setError(reason instanceof Error?reason.message:"Falha ao recuperar a solicitação.");
    }finally{setSending(false);}
  }

  async function confirmReport(){
    if(!accessToken||!identity||!ids||!reportStatus||sending)return;
    setSending(true);setError("");
    try{
      const next=await confirmTextReport(
        accessToken,identity,ids,reportStatus.confirmation.planHash,reportStatus.plan,
      );
      setReportStatus({...reportStatus,confirmation:next});
    }catch(reason){
      if(reason instanceof PlanError&&reason.status===503){
        try{
          const current=await getTextReportStatus(accessToken,identity,ids);
          setReportStatus(current);
        }catch{}
        setError("A criação está em conferência. Use Atualizar situação; o relatório não será reenviado.");
      }else{
        setError(reason instanceof Error?reason.message:"Não foi possível confirmar a criação do relatório.");
      }
    }finally{setSending(false);}
  }

  function openReport(){
    const reportId=reportStatus?.confirmation.report?.id;
    if(!reportId)return;
    window.open(analyticsReportUrl(reportId),"_blank","noopener,noreferrer");
  }

  if(loading)return <OktonLoadingState title="Carregando piloto da IA..." minH="180px"/>;

  const reportState=reportStatus?.confirmation.state;
  const preview=reportStatus?.preview.data;
  const transientExecution=reportStatus?.confirmation.report?.transientExecution;
  const reportActionLabel=reportState==="draft"?"Confirmar criação":"Atualizar situação";
  const filterLabel=(fieldId:number)=>{
    const field=preview?.filters.find(item=>Number(item.id??item.field_id)===fieldId);
    return typeof field?.label==="string"&&field.label.trim()?field.label:`Campo #${fieldId}`;
  };

  return <Stack spacing="6">
    <OktonPanel title="Teste real do piloto" subtitle="Executa o fluxo contratado com empresa, filial, plano, Router e contabilização reais">
      <Stack spacing="4">
        {error&&<OktonAlert status="error"><Text>{error}</Text></OktonAlert>}
        <SimpleGrid columns={[1,2,3]} spacing="4">
          <Box borderWidth="1px" rounded="md" p="4">
            <Text fontSize="xs">Empresa / filial</Text>
            <Text fontWeight="700">{context?context.companyId+" / "+context.branchId:"Não definido"}</Text>
            <Text fontSize="xs">{context?.branchName||""}</Text>
          </Box>
          <Box borderWidth="1px" rounded="md" p="4">
            <Text fontSize="xs">Contrato</Text>
            <Text fontWeight="700">{plan?.current?.snapshot.name||"Sem plano contratado"}</Text>
            <Badge mt="1">{available?"Geração habilitada":"Geração indisponível"}</Badge>
          </Box>
          <Box borderWidth="1px" rounded="md" p="4">
            <Text fontSize="xs">Saldo disponível</Text>
            <Text fontWeight="700">{usage?textMoney(usage.availableMicros):"-"}</Text>
            <Text fontSize="xs">Confirmado: {usage?textMoney(usage.confirmedMicros):"-"}</Text>
          </Box>
        </SimpleGrid>

        <Box>
          <Text mb="2" fontSize="sm" fontWeight="600">Pergunta</Text>
          <Textarea value={input} onChange={e=>setInput(e.target.value)} minH="120px" maxLength={16384}/>
        </Box>
        <Stack direction={["column","row"]} spacing="2">
          <Button colorScheme="blue" onClick={send} isLoading={sending} isDisabled={!available||!identity||!input.trim()}>
            Enviar teste real
          </Button>
          <Button variant="outline" onClick={recover} isLoading={sending} isDisabled={!ids}>
            {reportStatus?"Atualizar situação":"Recuperar mesma solicitação"}
          </Button>
          <Button variant="ghost" onClick={()=>refresh()} isDisabled={sending}>Atualizar saldo</Button>
        </Stack>
        <Text fontSize="xs">
          O envio pode consumir saldo real do plano. Recuperar ou atualizar uma solicitação nunca cria um novo requestId/callId.
        </Text>
      </Stack>
    </OktonPanel>

    {reportStatus&&<OktonPanel title={reportState==="saved"?"Relatório criado":"Relatório proposto"} subtitle="Plano montado com os datasets e campos atuais do Analytics">
      <Stack spacing="4">
        <Stack direction={["column","row"]} justify="space-between" align={["flex-start","center"]}>
          <Box>
            <Text fontWeight="700">{reportStatus.plan.title}</Text>
            <Text fontSize="sm">Dataset: {preview?.dataset.name||`#${reportStatus.plan.datasetId}`}</Text>
          </Box>
          {reportState&&<Badge colorScheme={confirmationColors[reportState]}>{confirmationLabels[reportState]}</Badge>}
        </Stack>
        <Box>
          <Text fontSize="sm" fontWeight="600">Colunas</Text>
          <Text fontSize="sm">{preview?.columns.map(column=>column.label).join(", ")||"-"}</Text>
        </Box>
        <Box>
          <Text fontSize="sm" fontWeight="600">Agrupamentos</Text>
          <Text fontSize="sm">{preview?.groups.length?preview.groups.map(group=>group.label).join(", "):"Sem agrupamento"}</Text>
        </Box>
        {preview?.rows.slice(0,3).map((row,index)=><Box key={index} borderWidth="1px" rounded="md" p="3">
          {preview.columns.slice(0,6).map(column=><Text key={column.key} fontSize="sm"><b>{column.label}:</b> {String(row[column.key]??"-")}</Text>)}
        </Box>)}
        {reportStatus.plan.filters.length>0&&reportState!=="saved"&&<OktonAlert status="info"><Stack spacing="1">
          <Text fontSize="sm">Os valores abaixo serão usados somente na primeira execução após a confirmação. O relatório salvará apenas os campos de filtro, sem deixar período ou valor fixo para consultas futuras.</Text>
          {reportStatus.plan.filters.map(filter=><Text key={filter.fieldId} fontSize="sm"><b>{filterLabel(filter.fieldId)}:</b> {formatFilterValue(filter.value)||"sem valor"}</Text>)}
        </Stack></OktonAlert>}
        {transientExecution&&<OktonAlert status="success"><Stack spacing="1">
          <Text fontSize="sm" fontWeight="700">Filtros aplicados somente nesta execução</Text>
          <Text fontSize="sm">Os valores não foram gravados como padrão do relatório. Ao abrir ou executar novamente, os filtros começam livres.</Text>
          {reportStatus.plan.filters.map(filter=><Text key={filter.fieldId} fontSize="sm"><b>{filterLabel(filter.fieldId)}:</b> {formatFilterValue(filter.value)||"sem valor"}</Text>)}
          <Text fontSize="sm"><b>Registros retornados:</b> {transientExecution.count} de até {transientExecution.limit}</Text>
          {transientExecution.summary?.totals.map(total=><Text key={`${total.fieldId}:${total.key}`} fontSize="sm"><b>{total.label}:</b> {formatTransientNumber(total.value)}</Text>)}
        </Stack></OktonAlert>}
        {reportState==="saved"?<Stack direction={["column","row"]}>
          <Button colorScheme="green" onClick={openReport}>Abrir relatório salvo</Button>
          <Button variant="outline" onClick={()=>window.open("https://analytics.okton.com.br/dashboard/my-reports","_blank","noopener,noreferrer")}>Abrir Meus Relatórios</Button>
        </Stack>:reportState!=="failed"&&<Button colorScheme="blue" alignSelf="flex-start" onClick={confirmReport} isLoading={sending}>
          {reportActionLabel}
        </Button>}
        <Text fontSize="xs" wordBreak="break-all">Hash do plano: {reportStatus.confirmation.planHash}</Text>
      </Stack>
    </OktonPanel>}

    {execution&&<OktonPanel title="Resultado" subtitle="Resposta e contabilização da solicitação atual">
      <Stack spacing="3">
        <Text><b>Estado:</b> {textStatuses[execution.call.state]}</Text>
        <Text><b>Reservado:</b> {textMoney(execution.call.estimate.brlMicros)}</Text>
        <Text><b>Custo confirmado do contrato:</b> {execution.call.actual?textMoney(execution.call.actual.brlMicros):"Ainda não confirmado"}</Text>
        {execution.result?.source&&<Text><b>Fonte:</b> {execution.result.source}</Text>}
        {execution.result?.cost?.customerChargeMicros!==undefined&&<Text><b>Valor comercial:</b> {textMoney(execution.result.cost.customerChargeMicros)}</Text>}
        {execution.result?.receivedText&&<OktonAlert status="warning"><Stack spacing="2"><Text fontWeight="700">Resposta recebida — custo em conferência</Text><Text whiteSpace="pre-wrap">{execution.result.receivedText}</Text></Stack></OktonAlert>}
        {execution.call.state==="uncertain"&&!execution.result?.receivedText&&<OktonAlert status="warning"><Text>Esta solicitação não possui texto preservado. Se ela foi enviada antes desta correção, o texto antigo não pode ser reconstruído sem uma nova chamada ao provedor.</Text></OktonAlert>}
        {execution.result?.text&&<Box borderWidth="1px" rounded="md" p="4"><Text whiteSpace="pre-wrap">{execution.result.text}</Text></Box>}
        {ids&&<Text fontSize="xs"><b>Request:</b> {ids.requestId}<br/><b>Call:</b> {ids.callId}</Text>}
      </Stack>
    </OktonPanel>}
  </Stack>;
};
